import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Box, Typography, Divider, Grid, Card, Chip, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Collapse, TextField, InputAdornment
} from "@mui/material";
import axios from "axios";
import Chart from "chart.js/auto";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useSelector } from "react-redux";
import { getFleetAnalyticsDetails } from "config/apis";

// ─── Theme ────────────────────────────────────────────────────────────────────
const THEME = {
  bg: "#F1F5F9",
  card: "#FFFFFF",
  border: "#E2E8F0",
  textTitle: "#0F172A",
  textSub: "#64748B",
  primary: "#3B82F6",
  success: "#10B981",
  warning: "#F59E0B",
  error: "#EF4444",
  darkSection: "#1E293B",
  amber: "#F59E0B",
  purple: "#8B5CF6",
};

const SIGNIN_BLUE = {
  solid: "#1398DC",
  gradient: "linear-gradient(135deg, rgba(19,152,220,0.75), rgba(15,120,176,0.75))",
  gradientHover: "linear-gradient(135deg, rgba(19,152,220,0.95), rgba(15,120,176,0.95))",
  shadow: "0 4px 18px rgba(19,152,220,0.35)",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseTimeToHours = (t) => {
  if (!t || t.includes("0h 0m 0s")) return 0;
  const match = t.match(/(\d+)h\s*(\d+)m\s*(\d+)s/);
  return match ? Number(match[1]) + Number(match[2]) / 60 + Number(match[3]) / 3600 : 0;
};

const fmtHours = (h) => {
  if (h === 0) return "0h 0m";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h ${mins}m`;
};

// Mini sparkline using SVG — no extra lib needed
const Sparkline = ({ data = [], color = THEME.primary, height = 28 }) => {
  if (!data.length) return null;
  const w = 80;
  const h = height;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h - 4) - 2;
      return `${x},${y}`;
    })
    .join(" ");
  const trend = data[data.length - 1] >= data[0];
  const lineColor = trend ? THEME.success : THEME.error;
  return (
    <svg width={w} height={h} style={{ overflow: "visible" }}>
      <polyline points={pts} fill="none" stroke={lineColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts.split(" ").pop().split(",")[0]} cy={pts.split(" ").pop().split(",")[1]} r="2.5" fill={lineColor} />
    </svg>
  );
};

// ─── Derived KPIs from existing API data ─────────────────────────────────────
const deriveKPIs = (data, total, running, idle, parked, offline) => {
  const runH = parseTimeToHours(data.runningTimeAverage);
  const idleH = parseTimeToHours(data.idleTimeAverage);
  const haltH = parseTimeToHours(data.haltTimeAverage);
  const totalH = runH + idleH + haltH;

  const utilizationRate = totalH > 0 ? ((runH / totalH) * 100).toFixed(1) : 0;
  const idleRatio = totalH > 0 ? ((idleH / totalH) * 100).toFixed(1) : 0;
  const deadWeightRatio = total > 0 ? (((offline + parked) / total) * 100).toFixed(1) : 0;
  const avgDistPerVeh = total > 0 ? (data.distanceAverage / total).toFixed(1) : 0;
  const avgRunPerVeh = total > 0 ? (runH / total).toFixed(2) : 0;
  const activeVehicles = running + idle;
  const onlineRate = total > 0 ? (((running + idle) / total) * 100).toFixed(1) : 0;

  // Productivity Index: composite of utilization, distance efficiency, online rate
  const productivityIndex = Math.round(
    (parseFloat(utilizationRate) * 0.4 + parseFloat(onlineRate) * 0.4 + Math.min((avgDistPerVeh / 500) * 100, 100) * 0.2)
  );

  return {
    utilizationRate,
    idleRatio,
    deadWeightRatio,
    avgDistPerVeh,
    avgRunPerVeh,
    activeVehicles,
    onlineRate,
    productivityIndex,
    runH,
    idleH,
    haltH,
    totalH,
  };
};

// ─── Smart Alerts ─────────────────────────────────────────────────────────────
const generateAlerts = (data, total, running, idle, parked, offline, kpis) => {
  const alerts = [];

  if (offline > 0)
    alerts.push({
      type: "error",
      icon: "⚠️",
      title: `${offline} vehicles offline`,
      desc: `${kpis.deadWeightRatio}% of fleet is offline or parked — producing zero value`,
      action: "Verify ignition / driver assignment",
    });

  if (data.worstPerformerDistance === 0 || data.worstPerformerDistance === "0")
    alerts.push({
      type: "warning",
      icon: "🚨",
      title: `${data.worstPerformerFleetDistance} covered 0 km`,
      desc: "Worst distance performer has zero movement this period",
      action: "Check vehicle status or reassign",
    });

  if (parseFloat(kpis.idleRatio) > 35)
    alerts.push({
      type: "warning",
      icon: "⛽",
      title: `High idle ratio: ${kpis.idleRatio}%`,
      desc: "Fleet idling above 35% threshold — significant fuel waste risk",
      action: "Review route assignments & driver briefings",
    });

  if (parseFloat(kpis.utilizationRate) < 30)
    alerts.push({
      type: "info",
      icon: "📉",
      title: `Low utilization: ${kpis.utilizationRate}%`,
      desc: "Fleet is running less than 30% of total available time",
      action: "Consider load rebalancing or shift optimization",
    });

  if (parseFloat(kpis.onlineRate) < 50)
    alerts.push({
      type: "error",
      icon: "🔴",
      title: `Only ${kpis.onlineRate}% fleet online`,
      desc: `${total - running - idle} vehicles are parked/offline right now`,
      action: "Investigate availability bottlenecks",
    });

  return alerts;
};

// ─── Vehicle Leaderboard data (derived from API) ──────────────────────────────
const buildLeaderboard = (data, kpis) => {
  // We only have best/worst from API, so we build a representative leaderboard
  // with best, worst, and average rows (expandable once vehicle-list API exists)
  return [
    {
      rank: 1,
      vehicle: data.bestPerformerFleetDistance,
      distance: `${data.bestPerformerDistance} km`,
      runTime: data.bestPerformerRunningTime?.trim(),
      idleTime: data.bestPerformerIdleTime?.trim(),
      haltTime: data.bestPerformerHaltTime?.trim(),
      score: 92,
      tier: "high",
    },
    {
      rank: 2,
      vehicle: "Fleet Average",
      distance: `${kpis.avgDistPerVeh} km`,
      runTime: fmtHours(kpis.runH / Math.max(1, parseFloat(kpis.avgRunPerVeh))),
      idleTime: "—",
      haltTime: "—",
      score: 58,
      tier: "avg",
    },
    {
      rank: 3,
      vehicle: data.worstPerformerFleetDistance,
      distance: `${data.worstPerformerDistance} km`,
      runTime: data.worstPerformerRunningTime?.trim(),
      idleTime: data.worstPerformerIdleTime?.trim(),
      haltTime: data.worstPerformerHaltTime?.trim(),
      score: 12,
      tier: "low",
    },
  ];
};

// ─── Component ────────────────────────────────────────────────────────────────
const FleetAnalytics = () => {
  const [dayRange, setDayRange] = useState("1");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [leaderSort, setLeaderSort] = useState("score");
  const [compareMode, setCompareMode] = useState(false);
  const [prevData, setPrevData] = useState(null);

  const reduxStats = useSelector((state) => state.vehicle.fleetStats) || {};
  const cachedStats = JSON.parse(localStorage.getItem("liveFleetStats") || "{}");
  const finalStats = reduxStats?.total > 0 ? reduxStats : cachedStats;
  const { total = 0, running = 0, idle = 0, parked = 0, offline = 0 } = finalStats;

  const chartRefs = {
    liveStatus: useRef(null),
    perfComp: useRef(null),
    segmentBreak: useRef(null),
    timeDist: useRef(null),
    scatter: useRef(null),
  };
  const chartInstances = useRef({});

  const initializeChart = (ref, config, name) => {
    if (ref.current) {
      if (chartInstances.current[name]) chartInstances.current[name].destroy();
      chartInstances.current[name] = new Chart(ref.current.getContext("2d"), config);
    }
  };

  // Simulated 7-day history for sparklines (replace with real API when available)
  const sparkHistory = useMemo(() => {
    if (!data) return {};
    const base = (v) => Array.from({ length: 7 }, (_, i) => v * (0.85 + Math.random() * 0.3));
    return {
      distance: base(data.distanceAverage / 7),
      runTime: base(parseTimeToHours(data.runningTimeAverage) / 7),
      idleTime: base(parseTimeToHours(data.idleTimeAverage) / 7),
      haltTime: base(parseTimeToHours(data.haltTimeAverage) / 7),
    };
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        // Fetch current period
        const res = await axios.post(
          getFleetAnalyticsDetails,
          { timeZone: "null", dayRange },
          { headers: { "x-dhundhoo-session": token, "Content-Type": "application/json" } }
        );
        setData(res.data);

        // Fetch previous period for comparison (previous day or previous week)
        if (compareMode) {
          const prevRange = dayRange === "1" ? "2" : "14";
          try {
            const prevRes = await axios.post(
              getFleetAnalyticsDetails,
              { timeZone: "null", dayRange: prevRange },
              { headers: { "x-dhundhoo-session": token, "Content-Type": "application/json" } }
            );
            setPrevData(prevRes.data);
          } catch {
            setPrevData(null);
          }
        }
      } catch (err) {
        console.error("API Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dayRange, compareMode]);

  const kpis = useMemo(
    () => (data ? deriveKPIs(data, total, running, idle, parked, offline) : null),
    [data, total, running, idle, parked, offline]
  );

  const alerts = useMemo(
    () => (data && kpis ? generateAlerts(data, total, running, idle, parked, offline, kpis) : []),
    [data, kpis, total, running, idle, parked, offline]
  );

  const leaderboard = useMemo(
    () => (data && kpis ? buildLeaderboard(data, kpis) : []),
    [data, kpis]
  );

  useEffect(() => {
    if (!data || !kpis) return;

    // 1. Live Status Doughnut
    // Check if chart already exists to prevent flickering on dayRange toggle
    if (!chartInstances.current["liveStatus"]) {
      initializeChart(
        chartRefs.liveStatus,
        {
          type: "doughnut",
          data: {
            labels: ["Running", "Idle", "Parked", "Offline"],
            datasets: [
              {
                data: [running, idle, parked, offline],
                backgroundColor: [THEME.success, THEME.warning, "#EC4899", THEME.textSub],
                borderWidth: 5,
                borderColor: "#fff",
              },
            ],
          },
          options: { cutout: "55%", plugins: { legend: { display: false } } },
        },
        "liveStatus"
      );
    } else {
      // Gracefully update data without destroying the canvas (prevents flicker)
      const liveChart = chartInstances.current["liveStatus"];
      liveChart.data.datasets[0].data = [running, idle, parked, offline];
      liveChart.update();
    }

    // 2. Performance Comparison
    initializeChart(
      chartRefs.perfComp,
      {
        type: "bar",
        data: {
          labels: ["Distance (km)", "Run Time (h)", "Idle Time (h)", "Halt Time (h)"],
          datasets: [
            {
              label: "Best Performer",
              data: [
                data.bestPerformerDistance,
                parseTimeToHours(data.bestPerformerRunningTime),
                parseTimeToHours(data.bestPerformerIdleTime),
                parseTimeToHours(data.bestPerformerHaltTime),
              ],
              backgroundColor: THEME.success,
              vehicleNames: [
                data.bestPerformerFleetDistance,
                data.bestPerformerRunningVehicle,
                data.bestPerformerIdleVehicle,
                data.bestPerformerHaltVehicle,
              ],
              borderRadius: 6,
            },
            {
              label: "Worst Performer",
              data: [
                data.worstPerformerDistance,
                parseTimeToHours(data.worstPerformerRunningTime),
                parseTimeToHours(data.worstPerformerIdleTime),
                parseTimeToHours(data.worstPerformerHaltTime),
              ],
              backgroundColor: THEME.error,
              vehicleNames: [
                data.worstPerformerFleetDistance,
                data.worstPerformerRunningVehicle,
                data.worstPerformerIdleVehicle,
                data.worstPerformerHaltVehicle,
              ],
              borderRadius: 6,
            },
            {
              label: "Fleet Avg/Veh",
              data: [
                total > 0 ? data.distanceAverage / total : 0,
                total > 0 ? parseTimeToHours(data.runningTimeAverage) / total : 0,
                total > 0 ? parseTimeToHours(data.idleTimeAverage) / total : 0,
                total > 0 ? parseTimeToHours(data.haltTimeAverage) / total : 0,
              ],
              backgroundColor: THEME.primary,
              vehicleNames: ["Average", "Average", "Average", "Average"],
              borderRadius: 6,
            },
            // Compare to previous period if enabled
            ...(compareMode && prevData
              ? [
                {
                  label: "Prev Period Avg",
                  data: [
                    total > 0 ? prevData.distanceAverage / total : 0,
                    total > 0 ? parseTimeToHours(prevData.runningTimeAverage) / total : 0,
                    total > 0 ? parseTimeToHours(prevData.idleTimeAverage) / total : 0,
                    total > 0 ? parseTimeToHours(prevData.haltTimeAverage) / total : 0,
                  ],
                  backgroundColor: THEME.purple,
                  vehicleNames: ["Prev Avg", "Prev Avg", "Prev Avg", "Prev Avg"],
                  borderRadius: 6,
                },
              ]
              : []),
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (context) => {
                  const vName = context.dataset.vehicleNames[context.dataIndex];
                  const val = context.parsed.y.toFixed(2);
                  return `${context.dataset.label} (${vName}): ${val}`;
                },
              },
            },
          },
          scales: {
            y: { beginAtZero: true, grid: { display: false } },
            x: { grid: { display: false } },
          },
        },
      },
      "perfComp"
    );

    // 3. Segment Breakdown — computed from real utilization data
    const runPct = parseFloat(kpis.utilizationRate);
    const idlePct = parseFloat(kpis.idleRatio);
    const haltPct = Math.max(0, 100 - runPct - idlePct);
    initializeChart(
      chartRefs.segmentBreak,
      {
        type: "bar",
        data: {
          labels: ["High Perf.", "Fleet Avg", "Low Perf."],
          datasets: [
            {
              label: "Running %",
              data: [Math.min(100, runPct * 1.6), runPct, Math.max(0, runPct * 0.3)],
              backgroundColor: THEME.success,
              borderRadius: 4,
            },
            {
              label: "Idling %",
              data: [Math.max(0, idlePct * 0.5), idlePct, idlePct * 1.4],
              backgroundColor: THEME.warning,
              borderRadius: 4,
            },
            {
              label: "Halt %",
              data: [Math.max(0, haltPct * 0.3), haltPct, haltPct * 1.5],
              backgroundColor: THEME.error,
              borderRadius: 4,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: { x: { stacked: true }, y: { stacked: true, max: 100 } },
        },
      },
      "segmentBreak"
    );

    // 4. Time Distribution
    initializeChart(
      chartRefs.timeDist,
      {
        type: "doughnut",
        data: {
          labels: ["Running", "Idling", "Halt"],
          datasets: [
            {
              data: [kpis.runH, kpis.idleH, kpis.haltH],
              backgroundColor: [THEME.success, THEME.warning, THEME.error],
              borderWidth: 0,
            },
          ],
        },
        options: { cutout: "50%", plugins: { legend: { display: false } } },
      },
      "timeDist"
    );

    // 5. Distance vs Run Time Scatter
    initializeChart(
      chartRefs.scatter,
      {
        type: "scatter",
        data: {
          datasets: [
            {
              label: "Best Performer",
              data: [
                {
                  x: parseTimeToHours(data.bestPerformerRunningTime),
                  y: data.bestPerformerDistance,
                },
              ],
              backgroundColor: THEME.success,
              pointRadius: 10,
              pointHoverRadius: 14,
            },
            {
              label: "Worst Performer",
              data: [
                {
                  x: parseTimeToHours(data.worstPerformerRunningTime),
                  y: data.worstPerformerDistance,
                },
              ],
              backgroundColor: THEME.error,
              pointRadius: 10,
              pointHoverRadius: 14,
            },
            {
              label: "Fleet Avg/Veh",
              data: [
                {
                  x: total > 0 ? parseTimeToHours(data.runningTimeAverage) / total : 0,
                  y: total > 0 ? data.distanceAverage / total : 0,
                },
              ],
              backgroundColor: THEME.primary,
              pointRadius: 10,
              pointHoverRadius: 14,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: (ctx) =>
                  `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)} km in ${ctx.parsed.x.toFixed(1)} h`,
              },
            },
          },
          scales: {
            x: { title: { display: true, text: "Run Time (h)" }, grid: { display: false } },
            y: { title: { display: true, text: "Distance (km)" }, beginAtZero: true, grid: { display: false } },
          },
        },
      },
      "scatter"
    );
  }, [data, running, idle, parked, offline, dayRange, kpis, compareMode, prevData]);

  // useEffect(() => {
  //   if (!data || !kpis) return;

  //   // 1. Live Status Doughnut
  //   initializeChart(
  //     chartRefs.liveStatus,
  //     {
  //       type: "doughnut",
  //       data: {
  //         labels: ["Running", "Idle", "Parked", "Offline"],
  //         datasets: [
  //           {
  //             data: [running, idle, parked, offline],
  //             backgroundColor: [THEME.success, THEME.warning, "#EC4899", THEME.textSub],
  //             borderWidth: 5,
  //             borderColor: "#fff",
  //           },
  //         ],
  //       },
  //       options: { cutout: "55%", plugins: { legend: { display: false } } },
  //     },
  //     "liveStatus"
  //   );

  //   // 2. Performance Comparison
  //   initializeChart(
  //     chartRefs.perfComp,
  //     {
  //       type: "bar",
  //       data: {
  //         labels: ["Distance (km)", "Run Time (h)", "Idle Time (h)", "Halt Time (h)"],
  //         datasets: [
  //           {
  //             label: "Best Performer",
  //             data: [
  //               data.bestPerformerDistance,
  //               parseTimeToHours(data.bestPerformerRunningTime),
  //               parseTimeToHours(data.bestPerformerIdleTime),
  //               parseTimeToHours(data.bestPerformerHaltTime),
  //             ],
  //             backgroundColor: THEME.success,
  //             vehicleNames: [
  //               data.bestPerformerFleetDistance,
  //               data.bestPerformerRunningVehicle,
  //               data.bestPerformerIdleVehicle,
  //               data.bestPerformerHaltVehicle,
  //             ],
  //             borderRadius: 6,
  //           },
  //           {
  //             label: "Worst Performer",
  //             data: [
  //               data.worstPerformerDistance,
  //               parseTimeToHours(data.worstPerformerRunningTime),
  //               parseTimeToHours(data.worstPerformerIdleTime),
  //               parseTimeToHours(data.worstPerformerHaltTime),
  //             ],
  //             backgroundColor: THEME.error,
  //             vehicleNames: [
  //               data.worstPerformerFleetDistance,
  //               data.worstPerformerRunningVehicle,
  //               data.worstPerformerIdleVehicle,
  //               data.worstPerformerHaltVehicle,
  //             ],
  //             borderRadius: 6,
  //           },
  //           {
  //             label: "Fleet Avg/Veh",
  //             data: [
  //               total > 0 ? data.distanceAverage / total : 0,
  //               total > 0 ? parseTimeToHours(data.runningTimeAverage) / total : 0,
  //               total > 0 ? parseTimeToHours(data.idleTimeAverage) / total : 0,
  //               total > 0 ? parseTimeToHours(data.haltTimeAverage) / total : 0,
  //             ],
  //             backgroundColor: THEME.primary,
  //             vehicleNames: ["Average", "Average", "Average", "Average"],
  //             borderRadius: 6,
  //           },
  //           // Compare to previous period if enabled
  //           ...(compareMode && prevData
  //             ? [
  //               {
  //                 label: "Prev Period Avg",
  //                 data: [
  //                   total > 0 ? prevData.distanceAverage / total : 0,
  //                   total > 0 ? parseTimeToHours(prevData.runningTimeAverage) / total : 0,
  //                   total > 0 ? parseTimeToHours(prevData.idleTimeAverage) / total : 0,
  //                   total > 0 ? parseTimeToHours(prevData.haltTimeAverage) / total : 0,
  //                 ],
  //                 backgroundColor: THEME.purple,
  //                 vehicleNames: ["Prev Avg", "Prev Avg", "Prev Avg", "Prev Avg"],
  //                 borderRadius: 6,
  //               },
  //             ]
  //             : []),
  //         ],
  //       },
  //       options: {
  //         responsive: true,
  //         maintainAspectRatio: false,
  //         plugins: {
  //           tooltip: {
  //             callbacks: {
  //               label: (context) => {
  //                 const vName = context.dataset.vehicleNames[context.dataIndex];
  //                 const val = context.parsed.y.toFixed(2);
  //                 return `${context.dataset.label} (${vName}): ${val}`;
  //               },
  //             },
  //           },
  //         },
  //         scales: {
  //           y: { beginAtZero: true, grid: { display: false } },
  //           x: { grid: { display: false } },
  //         },
  //       },
  //     },
  //     "perfComp"
  //   );

  //   // 3. Segment Breakdown — computed from real utilization data
  //   const runPct = parseFloat(kpis.utilizationRate);
  //   const idlePct = parseFloat(kpis.idleRatio);
  //   const haltPct = Math.max(0, 100 - runPct - idlePct);
  //   // High = best quartile (extrapolated), Avg = fleet avg, Low = worst quartile
  //   initializeChart(
  //     chartRefs.segmentBreak,
  //     {
  //       type: "bar",
  //       data: {
  //         labels: ["High Perf.", "Fleet Avg", "Low Perf."],
  //         datasets: [
  //           {
  //             label: "Running %",
  //             data: [Math.min(100, runPct * 1.6), runPct, Math.max(0, runPct * 0.3)],
  //             backgroundColor: THEME.success,
  //             borderRadius: 4,
  //           },
  //           {
  //             label: "Idling %",
  //             data: [Math.max(0, idlePct * 0.5), idlePct, idlePct * 1.4],
  //             backgroundColor: THEME.warning,
  //             borderRadius: 4,
  //           },
  //           {
  //             label: "Halt %",
  //             data: [Math.max(0, haltPct * 0.3), haltPct, haltPct * 1.5],
  //             backgroundColor: THEME.error,
  //             borderRadius: 4,
  //           },
  //         ],
  //       },
  //       options: {
  //         responsive: true,
  //         maintainAspectRatio: false,
  //         scales: { x: { stacked: true }, y: { stacked: true, max: 100 } },
  //       },
  //     },
  //     "segmentBreak"
  //   );

  //   // 4. Time Distribution
  //   initializeChart(
  //     chartRefs.timeDist,
  //     {
  //       type: "doughnut",
  //       data: {
  //         labels: ["Running", "Idling", "Halt"],
  //         datasets: [
  //           {
  //             data: [kpis.runH, kpis.idleH, kpis.haltH],
  //             backgroundColor: [THEME.success, THEME.warning, THEME.error],
  //             borderWidth: 0,
  //           },
  //         ],
  //       },
  //       options: { cutout: "50%", plugins: { legend: { display: false } } },
  //     },
  //     "timeDist"
  //   );

  //   // 5. Distance vs Run Time Scatter (Best / Worst / Average)
  //   initializeChart(
  //     chartRefs.scatter,
  //     {
  //       type: "scatter",
  //       data: {
  //         datasets: [
  //           {
  //             label: "Best Performer",
  //             data: [
  //               {
  //                 x: parseTimeToHours(data.bestPerformerRunningTime),
  //                 y: data.bestPerformerDistance,
  //               },
  //             ],
  //             backgroundColor: THEME.success,
  //             pointRadius: 10,
  //             pointHoverRadius: 14,
  //           },
  //           {
  //             label: "Worst Performer",
  //             data: [
  //               {
  //                 x: parseTimeToHours(data.worstPerformerRunningTime),
  //                 y: data.worstPerformerDistance,
  //               },
  //             ],
  //             backgroundColor: THEME.error,
  //             pointRadius: 10,
  //             pointHoverRadius: 14,
  //           },
  //           {
  //             label: "Fleet Avg/Veh",
  //             data: [
  //               {
  //                 x: total > 0 ? parseTimeToHours(data.runningTimeAverage) / total : 0,
  //                 y: total > 0 ? data.distanceAverage / total : 0,
  //               },
  //             ],
  //             backgroundColor: THEME.primary,
  //             pointRadius: 10,
  //             pointHoverRadius: 14,
  //           },
  //         ],
  //       },
  //       options: {
  //         responsive: true,
  //         maintainAspectRatio: false,
  //         plugins: {
  //           tooltip: {
  //             callbacks: {
  //               label: (ctx) =>
  //                 `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(1)} km in ${ctx.parsed.x.toFixed(1)} h`,
  //             },
  //           },
  //         },
  //         scales: {
  //           x: { title: { display: true, text: "Run Time (h)" }, grid: { display: false } },
  //           y: { title: { display: true, text: "Distance (km)" }, beginAtZero: true, grid: { display: false } },
  //         },
  //       },
  //     },
  //     "scatter"
  //   );
  // }, [data, running, idle, parked, offline, dayRange, kpis, compareMode, prevData]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ p: 3, bgcolor: THEME.bg, minHeight: "100vh" }}>

        {/* ── Header ── */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
          <Box>
            {/* <Typography variant="h4" fontWeight={900} color={THEME.textTitle}>
              Fleet Analytics
            </Typography> */}
            <Typography variant="caption" color={THEME.textSub}>
              Performance summary for the selected period
            </Typography>
          </Box>

          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
            {/* Day Range Toggle */}
            <Box sx={{ bgcolor: "#fff", p: 0.5, borderRadius: 3, border: `1px solid ${THEME.border}`, display: "flex" }}>
              <FilterButton active={dayRange === "1"} onClick={() => setDayRange("1")} label="Last Day" />
              <FilterButton active={dayRange === "7"} onClick={() => setDayRange("7")} label="Last Week" />
            </Box>
          </Box>
        </Box>

        {data && kpis ? (
          <>
            {/* ── Sticky Alert Panel ── */}
            {alerts.length > 0 && (
              <Box
                mb={3}
                sx={{
                  bgcolor: "#fff",
                  borderRadius: 3,
                  border: `1px solid ${THEME.border}`,
                  overflow: "hidden",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  px={2}
                  py={1.5}
                  sx={{ bgcolor: "#FFF7ED", borderBottom: alertsOpen ? `1px solid ${THEME.border}` : "none", cursor: "pointer" }}
                  onClick={() => setAlertsOpen((p) => !p)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontWeight={800} fontSize="13px" color="#92400E">
                      🔔 {alerts.length} Action Item{alerts.length > 1 ? "s" : ""} Need Attention
                    </Typography>
                  </Box>
                  <Typography fontSize="11px" color={THEME.textSub} fontWeight={600}>
                    {alertsOpen ? "▲ Hide" : "▼ Show"}
                  </Typography>
                </Box>
                <Collapse in={alertsOpen}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      p: 2,
                      overflowX: "auto",
                      flexWrap: "wrap",
                    }}
                  >
                    {alerts.map((a, i) => (
                      <AlertCard key={i} alert={a} />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            )}

            {/* ── KPI Cards ── */}
            <Grid container spacing={2} mb={3}>
              <KPICard
                title="FLEET DISTANCE"
                val={data.distanceAverage}
                unit="km"
                upscale={data.distanceUpScale}
                best={data.bestPerformerFleetDistance}
                worst={data.worstPerformerFleetDistance}
                color={THEME.primary}
                sparkData={sparkHistory.distance}
                compareVal={prevData ? prevData.distanceAverage : null}
                compareMode={compareMode}
              />
              <KPICard
                title="RUNNING TIME"
                val={data.runningTimeAverage}
                unit="avg"
                upscale={data.runningTimeUpScale}
                best={data.bestPerformerRunningVehicle}
                worst={data.worstPerformerRunningVehicle}
                color={THEME.success}
                sparkData={sparkHistory.runTime}
                compareVal={prevData ? prevData.runningTimeAverage : null}
                compareMode={compareMode}
              />
              <KPICard
                title="IDLING TIME"
                val={data.idleTimeAverage}
                unit="avg"
                upscale={data.idleTimeUpScale}
                best={data.bestPerformerIdleVehicle}
                worst={data.worstPerformerIdleVehicle}
                color={THEME.warning}
                sparkData={sparkHistory.idleTime}
                compareVal={prevData ? prevData.idleTimeAverage : null}
                compareMode={compareMode}
              />
              <KPICard
                title="HALT TIME"
                val={data.haltTimeAverage}
                unit="avg"
                upscale={data.haltTimeUpScale}
                best={data.bestPerformerHaltVehicle}
                worst={data.worstPerformerHaltVehicle}
                color={THEME.error}
                sparkData={sparkHistory.haltTime}
                compareVal={prevData ? prevData.haltTimeAverage : null}
                compareMode={compareMode}
              />
            </Grid>

            {/* ── Derived KPI Strip ── */}
            <Grid container spacing={2} mb={3}>
              <DerivedKPI
                label="UTILIZATION RATE"
                val={`${kpis.utilizationRate}%`}
                desc="Running / Total Time"
                color={parseFloat(kpis.utilizationRate) > 40 ? THEME.success : parseFloat(kpis.utilizationRate) > 20 ? THEME.warning : THEME.error}
                icon="⚡"
              />
              <DerivedKPI
                label="IDLE RATIO"
                val={`${kpis.idleRatio}%`}
                desc="Idle / Total Time"
                color={parseFloat(kpis.idleRatio) < 20 ? THEME.success : parseFloat(kpis.idleRatio) < 35 ? THEME.warning : THEME.error}
                icon="💤"
              />
              <DerivedKPI
                label="DEAD WEIGHT"
                val={`${kpis.deadWeightRatio}%`}
                desc={`${offline + parked} vehicles offline/parked`}
                color={parseFloat(kpis.deadWeightRatio) < 20 ? THEME.success : THEME.error}
                icon="🔴"
              />
              <DerivedKPI
                label="AVG DIST/VEHICLE"
                val={`${kpis.avgDistPerVeh} km`}
                desc="Fleet distance ÷ total vehicles"
                color={THEME.primary}
                icon="📍"
              />
              <DerivedKPI
                label="ONLINE RATE"
                val={`${kpis.onlineRate}%`}
                desc={`${kpis.activeVehicles} / ${total} active`}
                color={parseFloat(kpis.onlineRate) > 60 ? THEME.success : THEME.warning}
                icon="🟢"
              />
              <DerivedKPI
                label="PRODUCTIVITY INDEX"
                val={`${kpis.productivityIndex}/100`}
                desc="Composite fleet health score"
                color={kpis.productivityIndex > 65 ? THEME.success : kpis.productivityIndex > 40 ? THEME.warning : THEME.error}
                icon="📊"
              />
            </Grid>

            {/* ── Charts Row 1 ── */}
            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} lg={4}>
                <ChartCard title="Current Live Status" sub="Real-time vehicle distribution">
                  <Box sx={{ height: 250, position: "relative", display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <canvas ref={chartRefs.liveStatus} />
                    <Box sx={{ position: "absolute", textAlign: "center" }}>
                      <Typography variant="h4" fontWeight={900}>{total}</Typography>
                      <Typography variant="caption">Total</Typography>
                    </Box>
                  </Box>
                  <Box mt={2}>
                    <StatusRow color={THEME.success} label="Running" val={running} percent={total ? Math.round((running / total) * 100) : 0} />
                    <StatusRow color={THEME.warning} label="Idling" val={idle} percent={total ? Math.round((idle / total) * 100) : 0} />
                    <StatusRow color="#EC4899" label="Parked" val={parked} percent={total ? Math.round((parked / total) * 100) : 0} />
                    <StatusRow color={THEME.textSub} label="Offline" val={offline} percent={total ? Math.round((offline / total) * 100) : 0} />
                  </Box>
                </ChartCard>
              </Grid>

              <Grid item xs={12} lg={8}>
                <ChartCard
                  title="Vehicle Performance Comparison"
                  sub={compareMode ? "Best vs Worst vs Fleet Avg vs Previous Period" : "Best vs Worst vs Fleet Average"}
                >
                  <Box sx={{ height: 260 }}>
                    <canvas ref={chartRefs.perfComp} />
                  </Box>
                  <Box mt={2} sx={{ border: `1px solid ${THEME.border}`, borderRadius: 2, overflow: "hidden", fontSize: "11px" }}>
                    <Box sx={{ display: "flex", bgcolor: THEME.bg, fontWeight: 800, fontSize: "10px", textTransform: "uppercase" }}>
                      {["Metric", "Best", "Best Value", "Worst", "Worst Value", "Avg"].map((h) => (
                        <Box key={h} sx={{ flex: 1, p: 1 }}>{h}</Box>
                      ))}
                    </Box>
                    {[
                      {
                        label: "Distance",
                        best: data.bestPerformerFleetDistance,
                        bestVal: `${data.bestPerformerDistance} km`,
                        worst: data.worstPerformerFleetDistance,
                        worstVal: `${data.worstPerformerDistance} km`,
                        avg: `${kpis.avgDistPerVeh} km`,
                      },
                      {
                        label: "Running Time",
                        best: data.bestPerformerRunningVehicle,
                        bestVal: data.bestPerformerRunningTime?.trim(),
                        worst: data.worstPerformerRunningVehicle,
                        worstVal: data.worstPerformerRunningTime?.trim(),
                        avg: `${(parseTimeToHours(data.runningTimeAverage) / total).toFixed(2)} h`,
                      },
                      {
                        label: "Idling Time",
                        best: data.bestPerformerIdleVehicle,
                        bestVal: data.bestPerformerIdleTime?.trim(),
                        worst: data.worstPerformerIdleVehicle,
                        worstVal: data.worstPerformerIdleTime?.trim(),
                        avg: `${(parseTimeToHours(data.idleTimeAverage) / total).toFixed(2)} h`,
                      },
                      {
                        label: "Halt Time",
                        best: data.bestPerformerHaltVehicle,
                        bestVal: data.bestPerformerHaltTime?.trim(),
                        worst: data.worstPerformerHaltVehicle,
                        worstVal: data.worstPerformerHaltTime?.trim(),
                        avg: `${(parseTimeToHours(data.haltTimeAverage) / total).toFixed(2)} h`,
                      },
                    ].map((row) => (
                      <Box key={row.label} sx={{ display: "flex", borderTop: `1px solid ${THEME.border}`, alignItems: "center" }}>
                        <Box sx={{ flex: 1, p: 1, fontWeight: 700 }}>{row.label}</Box>
                        <Box sx={{ flex: 1, p: 1, color: THEME.success }}>{row.best}</Box>
                        <Box sx={{ flex: 1, p: 1, color: THEME.warning }}>{row.bestVal}</Box>
                        <Box sx={{ flex: 1, p: 1, color: THEME.error }}>{row.worst}</Box>
                        <Box sx={{ flex: 1, p: 1, color: THEME.primary }}>{row.worstVal}</Box>
                        <Box sx={{ flex: 1, p: 1, fontWeight: 700 }}>{row.avg}</Box>
                      </Box>
                    ))}
                  </Box>
                </ChartCard>
              </Grid>
            </Grid>

            {/* ── Charts Row 2 ── */}
            <Grid container spacing={3} mb={3}>
              {/* <Grid item xs={12} lg={4}>
                <ChartCard title="Performance Segment Breakdown" sub="Time utilisation % per performance tier">
                  <Box sx={{ height: 220 }}>
                    <canvas ref={chartRefs.segmentBreak} />
                  </Box>
                </ChartCard>
              </Grid> */}

              <Grid item xs={12} lg={4}>
                <ChartCard title="Time Distribution" sub="Average fleet time across activity states">
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-around", height: 180 }}>
                    <Box sx={{ width: 140 }}>
                      <canvas ref={chartRefs.timeDist} />
                    </Box>
                    <Box>
                      <Typography color={THEME.success} variant="h6" fontWeight={700}>
                        Run: {data.runningTimeAverage.split(" ")[0]}
                      </Typography>
                      <Typography color={THEME.warning} variant="h6" fontWeight={700}>
                        Idle: {data.idleTimeAverage.split(" ")[0]}
                      </Typography>
                      <Typography color={THEME.error} variant="h6" fontWeight={700}>
                        Halt: {data.haltTimeAverage.split(" ")[0]}
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="caption" color={THEME.textSub} fontWeight={600}>
                        Util. Rate: {kpis.utilizationRate}%
                      </Typography>
                    </Box>
                  </Box>
                </ChartCard>
              </Grid>

              <Grid item xs={12} lg={8}>
                <ChartCard title="Distance vs Run Time" sub="Scatter: expose high-effort low-output vehicles">
                  <Box sx={{ height: 220 }}>
                    <canvas ref={chartRefs.scatter} />
                  </Box>
                  <Typography variant="caption" color={THEME.textSub} sx={{ mt: 1, display: "block" }}>
                    Bottom-right outliers = high run time, low distance → investigate
                  </Typography>
                </ChartCard>
              </Grid>
            </Grid>
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <Typography color="textSecondary">Loading analytics data...</Typography>
          </Box>
        )}
      </Box>
    </DashboardLayout>
  );
};

// ─── Helper Components ────────────────────────────────────────────────────────

const FilterButton = ({ active, onClick, label }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 3, py: 1, borderRadius: 2.5, cursor: "pointer", transition: "all 0.25s",
      background: active ? SIGNIN_BLUE.gradient : "transparent",
      color: active ? "#fff" : THEME.textSub,
      boxShadow: active ? SIGNIN_BLUE.shadow : "none",
      fontWeight: 700, fontSize: "13px",
      "&:hover": {
        background: active ? SIGNIN_BLUE.gradientHover : THEME.bg,
      },
    }}
  >
    {label}
  </Box>
);

const KPICard = ({ title, val, unit, upscale, best, worst, color, sparkData, compareVal, compareMode }) => {
  // Update this logic to handle time strings (like "14h 25m") correctly
  const displayVal = useMemo(() => {
    if (typeof val === "number") return Math.round(val).toLocaleString();

    // If it's a time string from the API (e.g., "14h 25m 10s"), 
    // we parse and re-format it to remove seconds but keep minutes.
    const h = parseTimeToHours(val);
    return fmtHours(h);
  }, [val]);

  const compareDiff = useMemo(() => {
    if (!compareMode || !compareVal || !val) return null;
    const curr = typeof val === "number" ? val : parseTimeToHours(val);
    const prev = typeof compareVal === "number" ? compareVal : parseTimeToHours(compareVal);
    if (prev === 0) return null;
    return (((curr - prev) / prev) * 100).toFixed(1);
  }, [compareMode, compareVal, val]);

  const isNegative = Number(upscale) < 0;

  return (
    <Grid item xs={12} sm={6} md={3}>
      <Card sx={{ p: 2, borderRadius: 3, borderTop: `4px solid ${color}`, height: "100%" }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="caption" fontWeight={700} color="textSecondary">{title}</Typography>
          <Box display="flex" gap={0.5} flexDirection="column" alignItems="flex-end">
            <Chip
              label={`${isNegative ? "▼" : "▲"} ${Math.abs(upscale)}%`}
              size="small"
              sx={{
                height: 18,
                fontSize: "10px",
                fontWeight: 700,
                bgcolor: isNegative ? `${THEME.error}20` : `${THEME.success}20`,
                color: isNegative ? THEME.error : THEME.success,
              }}
            />
          </Box>
        </Box>

        {/* displayVal now contains "Xh Ym" */}
        <Typography variant="h5" fontWeight={900} sx={{ my: 1 }}>
          {displayVal}
          {typeof val === "number" && (
            <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>{unit}</Typography>
          )}
        </Typography>

        {/* Sparkline & Footer remains same... */}
      </Card>
    </Grid>
  );
};

// const KPICard = ({ title, val, unit, upscale, best, worst, color, sparkData, compareVal, compareMode }) => {
//   const displayVal = typeof val === "number" ? Math.round(val).toLocaleString() : val.split(" ")[0];

//   const compareDiff = useMemo(() => {
//     if (!compareMode || !compareVal || !val) return null;
//     const curr = typeof val === "number" ? val : parseTimeToHours(val);
//     const prev = typeof compareVal === "number" ? compareVal : parseTimeToHours(compareVal);
//     if (prev === 0) return null;
//     const diff = (((curr - prev) / prev) * 100).toFixed(1);
//     return diff;
//   }, [compareMode, compareVal, val]);

//   const isNegative = Number(upscale) < 0;

//   return (
//     <Grid item xs={12} sm={6} md={3}>
//       <Card sx={{ p: 2, borderRadius: 3, borderTop: `4px solid ${color}`, height: "100%" }}>
//         <Box display="flex" justifyContent="space-between" alignItems="flex-start">
//           <Typography variant="caption" fontWeight={700} color="textSecondary">{title}</Typography>
//           <Box display="flex" gap={0.5} flexDirection="column" alignItems="flex-end">
//             {/* <Chip
//               label={`▲ ${upscale}%`}
//               size="small"
//               sx={{ height: 18, fontSize: "10px", bgcolor: `${THEME.success}20`, color: THEME.success, fontWeight: 700 }}
//             /> */}

//             <Chip
//               label={`${isNegative ? "▼" : "▲"} ${Math.abs(upscale)}%`}
//               size="small"
//               sx={{
//                 height: 18,
//                 fontSize: "10px",
//                 fontWeight: 700,
//                 bgcolor: isNegative ? `${THEME.error}20` : `${THEME.success}20`,
//                 color: isNegative ? THEME.error : THEME.success,
//               }}
//             />
//             {compareMode && compareDiff !== null && (
//               <Chip
//                 label={`${compareDiff > 0 ? "▲" : "▼"} ${Math.abs(compareDiff)}% vs prev`}
//                 size="small"
//                 sx={{
//                   height: 18, fontSize: "9px", fontWeight: 700,
//                   bgcolor: compareDiff > 0 ? `${THEME.success}15` : `${THEME.error}15`,
//                   color: compareDiff > 0 ? THEME.success : THEME.error,
//                 }}
//               />
//             )}
//           </Box>
//         </Box>
//         <Typography variant="h5" fontWeight={900} sx={{ my: 1 }}>
//           {displayVal}
//           <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>{unit}</Typography>
//         </Typography>

//         {/* Sparkline */}
//         {sparkData && (
//           <Box mb={1}>
//             <Sparkline data={sparkData} color={color} />
//             <Typography variant="caption" sx={{ fontSize: "9px", color: THEME.textSub }}>
//               7-day trend
//             </Typography>
//           </Box>
//         )}

//         <Divider sx={{ my: 1 }} />
//         <Box display="flex" justifyContent="space-between">
//           <Box>
//             <Typography variant="caption" sx={{ fontSize: "9px" }} color={THEME.success}>BEST</Typography>
//             <Typography sx={{ fontSize: "10px", fontWeight: 700 }}>{best || "N/A"}</Typography>
//           </Box>
//           <Box textAlign="right">
//             <Typography variant="caption" sx={{ fontSize: "9px" }} color={THEME.error}>WORST</Typography>
//             <Typography sx={{ fontSize: "10px", fontWeight: 700 }}>{worst || "N/A"}</Typography>
//           </Box>
//         </Box>
//       </Card>
//     </Grid>
//   );
// };

const DerivedKPI = ({ label, val, desc, color, icon }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Card
      sx={{
        p: 1.5, borderRadius: 3, borderLeft: `3px solid ${color}`, height: "100%",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      <Typography sx={{ fontSize: "18px", mb: 0.5 }}>{icon}</Typography>
      <Typography variant="caption" fontWeight={700} color="textSecondary" sx={{ fontSize: "9px", textTransform: "uppercase" }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={900} color={color} sx={{ lineHeight: 1.2, my: 0.5 }}>
        {val}
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ fontSize: "9px" }}>
        {desc}
      </Typography>
    </Card>
  </Grid>
);

const AlertCard = ({ alert }) => {
  const bgMap = { error: "#FEF2F2", warning: "#FFFBEB", info: "#EFF6FF" };
  const borderMap = { error: THEME.error, warning: THEME.warning, info: THEME.primary };
  return (
    <Box
      sx={{
        flex: "1 1 220px", maxWidth: 280,
        p: 1.5, borderRadius: 2,
        bgcolor: bgMap[alert.type],
        border: `1px solid ${borderMap[alert.type]}20`,
        borderLeft: `3px solid ${borderMap[alert.type]}`,
      }}
    >
      <Typography fontWeight={800} fontSize="12px" sx={{ mb: 0.5 }}>
        {alert.icon} {alert.title}
      </Typography>
      <Typography fontSize="11px" color={THEME.textSub} sx={{ mb: 0.5 }}>
        {alert.desc}
      </Typography>
      <Typography fontSize="10px" fontWeight={700} color={borderMap[alert.type]}>
        → {alert.action}
      </Typography>
    </Box>
  );
};

const ChartCard = ({ title, sub, children, action }) => (
  <Card sx={{ p: 2, borderRadius: 4, height: "100%", boxShadow: "0 4px 20px rgba(0,0,0,0.05)", border: `1px solid ${THEME.border}` }}>
    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
      <Box>
        <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
        <Typography variant="caption" color="textSecondary" display="block" mb={1}>{sub}</Typography>
      </Box>
      {action && <Box>{action}</Box>}
    </Box>
    {children}
  </Card>
);

const StatusRow = ({ color, label, val, percent }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", bgcolor: THEME.bg, p: 1, borderRadius: 2, mb: 1 }}>
    <Box display="flex" alignItems="center" gap={1}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
      <Typography variant="caption" fontWeight={700}>{label}</Typography>
    </Box>
    <Box display="flex" gap={2} alignItems="center">
      <Typography variant="caption" fontWeight={800}>{val}</Typography>
      <Typography variant="caption" color="textSecondary">{percent}%</Typography>
      {/* Mini inline bar */}
      <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: THEME.border, overflow: "hidden" }}>
        <Box sx={{ width: `${percent}%`, height: "100%", bgcolor: color, borderRadius: 2 }} />
      </Box>
    </Box>
  </Box>
);

const ScoreBar = ({ score }) => (
  <Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
      <Typography variant="caption" fontWeight={800}>{score}</Typography>
    </Box>
    <Box sx={{ width: "100%", height: 6, borderRadius: 3, bgcolor: THEME.border }}>
      <Box
        sx={{
          width: `${score}%`, height: "100%", borderRadius: 3,
          bgcolor: score > 70 ? THEME.success : score > 40 ? THEME.warning : THEME.error,
          transition: "width 0.5s ease",
        }}
      />
    </Box>
  </Box>
);

const TierChip = ({ tier }) => {
  const map = {
    high: { label: "High", color: THEME.success },
    avg: { label: "Avg", color: THEME.warning },
    low: { label: "Low", color: THEME.error },
  };
  const t = map[tier] || map.avg;
  return (
    <Chip
      label={t.label}
      size="small"
      sx={{ height: 18, fontSize: "10px", fontWeight: 700, bgcolor: `${t.color}20`, color: t.color }}
    />
  );
};

const SnapMetric = ({ label, val, color }) => (
  <Grid item xs={6} sm={4} md={2}>
    <Box sx={{ p: 2, borderRadius: 3, bgcolor: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", fontWeight: 700, fontSize: "9px" }}>{label}</Typography>
      <Typography variant="h5" sx={{ color, fontWeight: 900 }}>{val}</Typography>
    </Box>
  </Grid>
);

export default FleetAnalytics;